// The Cash instrument (GDD §15.1-1, M2 slice): balance, sparkline, income
// rate/yr, this-year's-interest as its own line, and the statement line.

import type { Params } from '@lifespan/engine';
import {
  incomeRate,
  latestStatement,
  thisYearLedger,
  type Session,
} from '../game/driver.ts';
import { ageLabel, money, moneyDelta } from '../game/format.ts';
import { Sparkline } from './Sparkline.tsx';

export function CashMeter({
  session,
  P,
  running,
}: {
  session: Session;
  P: Params;
  /** while time flows the statement changes every substep — live announcements
   *  off, or a screen reader gets a fresh announcement every ~5 s all run */
  running: boolean;
}) {
  const st = session.state;
  const stmt = latestStatement(session);
  const year = thisYearLedger(session);
  // round BEFORE choosing the sign: a −0.03 drift must render as unsigned
  // "0.0", never a signed zero ("−0.0" every substep near equilibrium)
  const dM = stmt ? Math.round(stmt.deltaM * 10) / 10 : 0;
  const dMsign = dM > 0 ? '+' : dM < 0 ? '−' : '';
  return (
    <section className="panel cash" aria-label="Cash">
      <div className="panel-title">Cash</div>
      <div className="cash-balance-row">
        <div className={`cash-balance ${st.S < 0 ? 'neg' : ''}`}>{money(st.S)}</div>
        {st.S < 0 && !st.brokeLock && (
          <span className="badge overdraft">Overdraft · {Math.round(P.rDebt * 100)}%/yr</span>
        )}
        {st.brokeLock && (
          <span className="badge broke">
            BROKE · Living locked to $0 · {Math.round(P.rDebt * 100)}%/yr
          </span>
        )}
      </div>
      <Sparkline samples={session.samples} />
      <div className="cash-lines">
        <div className="line">
          <span>Income</span>
          <span>{money(incomeRate(st, P))}/yr</span>
        </div>
        <div className="line">
          <span>Interest this year</span>
          <span>{money(year.interest, true)}</span>
        </div>
      </div>
      {stmt && (
        <div className="statement" aria-live={running ? 'off' : 'polite'}>
          {moneyDelta(stmt.deltaS)} this month · morale {dMsign}
          {Math.abs(dM).toFixed(1)} · age {ageLabel(stmt.age)}
        </div>
      )}
    </section>
  );
}
